import { Button } from "@arcadeai/design-system/components/ui/atoms/button";
import { Input } from "@arcadeai/design-system/components/ui/atoms/input";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { Plus, Search } from "lucide-react";
import { useMemo } from "react";
import { CanProject } from "@/app/capabilities/components/can-project";
import { PROJECT } from "@/app/capabilities/permissions";
import { ListLayout } from "@/components/custom/list-layout/list-layout";
import { EmptyResourceState } from "@/components/custom/resources/empty-resource-state";
import {
  ResourceContainer,
  type ResourceSection,
} from "@/components/custom/resources/resource-container";
import { ResourceViewSwitcher } from "@/components/custom/resources/resource-view-switcher";
import { useGetSecrets } from "../api/hooks";
import {
  calculateOverrides,
  filterSecretsBySearch,
  groupSecretsByBinding,
} from "../utils";
import { SecretSheet } from "./components/secret-sheet";
import { useSecretsViewOptions } from "./components/secrets-view-options";
import { SecretsTable } from "./components/table/secrets-table";

export const SecretList = () => {
  const { name: searchTerm = "" } = useSearch({
    from: "/_authenticated/orgs/$organizationId/projects/$projectId/auth/secrets/",
  });
  const navigate = useNavigate();

  const {
    columnVisibility,
    setColumnVisibility,
    component: ViewOptionsComponent,
  } = useSecretsViewOptions();

  const { data: allSecrets = [] } = useGetSecrets();

  // Filter and group secrets
  const secretsByBinding = useMemo(() => {
    if (!allSecrets.length) {
      return { project: [], tenant: [], static: [] };
    }

    const filteredSecrets = filterSecretsBySearch(allSecrets, searchTerm);
    return groupSecretsByBinding(filteredSecrets);
  }, [allSecrets, searchTerm]);

  const {
    project: projectSecrets,
    tenant: tenantSecrets,
    static: staticSecrets,
  } = secretsByBinding;

  // Calculate override relationships
  const overrides = useMemo(
    () => calculateOverrides(projectSecrets, tenantSecrets, staticSecrets),
    [projectSecrets, tenantSecrets, staticSecrets]
  );

  // Create sections for display
  const sections: ResourceSection[] = useMemo(() => {
    const sectionsToShow: ResourceSection[] = [];
    let isFirstSectionWithData = true;

    // Project Secrets
    if (projectSecrets.length > 0) {
      sectionsToShow.push({
        binding: "project",
        itemsCount: projectSecrets.length,
        resourceType: "secrets",
        defaultOpen: isFirstSectionWithData,
        children: (
          <SecretsTable
            columnVisibility={columnVisibility}
            items={projectSecrets}
            onColumnVisibilityChange={setColumnVisibility}
            overridingSecrets={overrides.overridingProjectSecrets}
          />
        ),
      });
      isFirstSectionWithData = false;
    }

    // Tenant Secrets
    if (tenantSecrets.length > 0) {
      sectionsToShow.push({
        binding: "tenant",
        itemsCount: tenantSecrets.length,
        resourceType: "secrets",
        defaultOpen: isFirstSectionWithData,
        overriddenCount: overrides.overriddenTenantSecrets.size,
        overriddenTooltipContent:
          "Some secrets are overridden by project secrets",
        children: (
          <SecretsTable
            columnVisibility={columnVisibility}
            items={tenantSecrets}
            onColumnVisibilityChange={setColumnVisibility}
            overriddenSecrets={overrides.overriddenTenantSecrets}
            overridingSecrets={overrides.overridingTenantSecrets}
          />
        ),
      });
      isFirstSectionWithData = false;
    }

    // Static Secrets
    if (staticSecrets.length > 0) {
      sectionsToShow.push({
        binding: "static",
        itemsCount: staticSecrets.length,
        resourceType: "secrets",
        defaultOpen: isFirstSectionWithData,
        overriddenCount: overrides.overriddenStaticSecrets.size,
        overriddenTooltipContent:
          "Some secrets are overridden by higher-level secrets",
        children: (
          <SecretsTable
            columnVisibility={columnVisibility}
            items={staticSecrets}
            onColumnVisibilityChange={setColumnVisibility}
            overriddenSecrets={overrides.overriddenStaticSecrets}
          />
        ),
      });
      isFirstSectionWithData = false;
    }

    return sectionsToShow;
  }, [
    projectSecrets,
    tenantSecrets,
    staticSecrets,
    columnVisibility,
    setColumnVisibility,
    overrides,
  ]);

  return (
    <ListLayout
      actions={
        <CanProject permission={PROJECT.secrets.create}>
          <SecretSheet
            trigger={
              <Button size="sm">
                <Plus className="mr-2 size-4" />
                Add Secret
              </Button>
            }
          />
        </CanProject>
      }
      description="Manage your secrets securely."
      filters={
        <>
          <div className="flex items-center gap-3">
            <div className="relative max-w-md">
              <Search className="absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="h-9 w-full pl-9"
                onChange={(event) => {
                  navigate({
                    to: ".",
                    search: (prev: Record<string, unknown>) => ({
                      ...prev,
                      name: event.target.value,
                    }),
                  });
                }}
                placeholder="Search secrets..."
                value={searchTerm}
              />
            </div>
            {ViewOptionsComponent}
          </div>
          <ResourceViewSwitcher />
        </>
      }
      title="Secrets"
    >
      {sections.length > 0 ? (
        <ResourceContainer
          defaultActiveTab={`${sections[0].binding}-${sections[0].resourceType}`}
          sections={sections}
        />
      ) : (
        <EmptyResourceState
          description={
            allSecrets.length > 0
              ? "No secrets match your search criteria. Try adjusting your search terms."
              : "Secrets allow you to securely store and manage sensitive information like API keys, database passwords, and configuration values across your environments."
          }
          resourceType={allSecrets.length > 0 ? "matching secrets" : "secrets"}
        />
      )}
    </ListLayout>
  );
};
